import { isMapId, mapInfo, MAPS, type MapId } from "@69ly/maps";
import { bombSecondsLeft, estimateClock, formatClock, LIVE_FORGET_MS, LIVE_STALE_MS, sameMatch, STATUSES, type LearnStatus, type LiveMember, type LiveState, type Side } from "@69ly/shared";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { LiveBoard } from "../components/live/LiveBoard.tsx";
import { GrenadeIcon } from "../components/MarkerIcon.tsx";
import { Select } from "../components/Select.tsx";
import { Loading } from "../components/Status.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { isFresh, useLiveFeed, useLiveSetup } from "../lib/live.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { useSetStatus, useStuffs } from "../lib/stuff-queries.ts";
import { useExecutions } from "../lib/teamplay-queries.ts";
import { useWakeLock } from "../lib/wake-lock.ts";

type PartyMode = "me" | "game" | "pick";

/** What the Live page shows when no game says otherwise. */
interface Manual {
  map: MapId;
  side: Side;
  players: string[];
}

const mapLabel = (map: string) => (isMapId(map) ? mapInfo(map).name : map.replace(/^de_/, ""));

// ------------------------------------------------------------------ header

function SideBadge({ side }: { side: Side | null }) {
  const { t } = useTranslation();
  if (!side) return <span className="live-side is-unknown">{t("live.sideUnknown")}</span>;
  return <span className={`live-side side-${side}`}>{t(`live.side.${side}`)}</span>;
}

/** The scores, my side first: "7 : 5". */
function scoreText(score: LiveState["score"], side: Side | null) {
  if (!score) return "– : –";
  return side === "ct" ? `${score.ct} : ${score.t}` : `${score.t} : ${score.ct}`;
}

function LiveHeader({
  map,
  side,
  status,
  tone = "live",
  score,
  sub,
}: {
  map: string | null;
  side?: Side | null;
  status: string;
  tone?: "live" | "warn" | "off";
  score?: string;
  sub?: string;
}) {
  const { t } = useTranslation();
  return (
    <header className="live-header">
      <div className="live-title">
        {map && isMapId(map) && <img src={mapIconUrl(map)} alt="" width={40} height={40} />}
        <div>
          <div className="live-title-row">
            <h1>{map ? mapLabel(map) : t("live.title")}</h1>
            {side !== undefined && map && <SideBadge side={side} />}
          </div>
          <p className={`live-status is-${tone}`}>
            <span className="live-dot" aria-hidden="true" />
            {status}
          </p>
        </div>
      </div>
      {score && (
        <div className="live-score">
          <span className="live-score-value">{score}</span>
          {sub && <span className="live-score-sub">{sub}</span>}
        </div>
      )}
    </header>
  );
}

function roundStatus(t: (k: string, o?: Record<string, unknown>) => string, s: LiveState, now: number): string {
  if (s.matchPhase === "warmup") return t("live.phase.warmup");
  if (s.matchPhase === "intermission") return t("live.phase.halftime");
  if (s.matchPhase === "gameover") return t("live.phase.gameover");
  if (s.roundPhase === "freezetime") return t("live.phase.freezetime");
  if (s.roundPhase === "over") return t("live.phase.over");
  if (s.bombPlantedAt) return t("live.bombPlanted");
  const clock = estimateClock(s, now);
  return clock !== null ? t("live.phase.liveClock", { clock: formatClock(clock) }) : t("live.phase.live");
}

/** Picked by hand: the map and the side change from the bar itself. */
interface Pick {
  onMap: (map: MapId) => void;
  onSide: (side: Side) => void;
}

/** "45 s", "3 min": how long the game has been quiet. */
function quietFor(t: (k: string, o?: Record<string, unknown>) => string, ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? t("live.seconds", { count: s }) : t("live.minutes", { count: Math.floor(s / 60) });
}

/**
 * The bar over the board, one line on a wide screen: the map and side, how the
 * round goes, who of the team is in this game, the score. Picked by hand, the map
 * is a list and the side a switch.
 */
function LiveTop({
  map,
  side,
  status,
  tone,
  score,
  sub,
  members,
  present,
  me,
  extra,
  notice,
  pick,
}: {
  map: string;
  side: Side | null;
  status: string;
  tone: "live" | "warn" | "off";
  score?: string;
  sub?: string;
  members: LiveMember[];
  present: Set<string>;
  me: string;
  extra?: ReactNode;
  notice?: ReactNode;
  pick?: Pick;
}) {
  const { t } = useTranslation();
  const ordered = [...members].sort((a, b) => Number(b.id === me) - Number(a.id === me) || Number(present.has(b.id)) - Number(present.has(a.id)));
  return (
    <div className="live-top">
      <header className="live-topbar">
        <div className="live-title">
          {isMapId(map) && !pick && <img src={mapIconUrl(map)} alt="" width={36} height={36} />}
          <div>
            {pick ? (
              <div className="live-title-row">
                <h1 className="visually-hidden">{mapLabel(map)}</h1>
                <Select
                  className="live-map-pick"
                  label={t("live.map")}
                  value={map}
                  placeholder={t("live.map")}
                  groups={[{ options: MAPS.filter((m) => m.activeDuty || m.id === map).map((m) => ({ value: m.id, label: m.name, icon: <img src={mapIconUrl(m.id)} alt="" width={26} height={26} /> })) }]}
                  onChange={(v) => isMapId(v) && pick.onMap(v)}
                />
                <div className="segmented segmented-small live-side-pick" role="group" aria-label={t("live.sideLabel")}>
                  {(["t", "ct"] as const).map((x) => (
                    <button key={x} type="button" className={`seg side-seg-${x}`} aria-pressed={side === x} onClick={() => side !== x && pick.onSide(x)}>
                      {t(`live.sideShort.${x}`)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="live-title-row">
                <h1>{mapLabel(map)}</h1>
                <SideBadge side={side} />
              </div>
            )}
            <p className={`live-status is-${tone}`}>
              <span className="live-dot" aria-hidden="true" />
              {status}
            </p>
          </div>
        </div>
        {members.length > 1 && (
          <div className="live-avatars live-here" role="group" aria-label={t("live.inGameCount", { count: present.size, total: members.length })}>
            {ordered.map((m) => {
              const here = present.has(m.id);
              const label = `${m.name}${m.id === me ? ` (${t("live.you")})` : ""}, ${here ? t("live.here") : t("live.notHere")}`;
              return (
                <span key={m.id} className={`live-avatar${here ? " is-here is-on" : ""}`} title={label} aria-label={label}>
                  <Avatar name={m.name} url={m.avatarUrl} size={28} />
                </span>
              );
            })}
          </div>
        )}
        {extra}
        {score && (
          <div className="live-score">
            <span className="live-score-value">{score}</span>
            {sub && <span className="live-score-sub">{sub}</span>}
          </div>
        )}
      </header>
      {notice}
    </div>
  );
}

/** Whose stuffs the board shows: mine, everyone in this game, or a choice (then the faces to pick). */
function PartyStrip({
  members,
  inGame,
  me,
  mode,
  onMode,
  picked,
  onPick,
}: {
  members: LiveMember[];
  inGame: Set<string>;
  me: string;
  mode: PartyMode;
  onMode: (m: PartyMode) => void;
  picked: Set<string>;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  const ordered = [...members].sort((a, b) => Number(b.id === me) - Number(a.id === me) || Number(inGame.has(b.id)) - Number(inGame.has(a.id)));
  return (
    <div className="live-party">
      <div className="segmented" role="group" aria-label={t("live.whose")}>
        {(["me", "game", "pick"] as const).map((m) => (
          <button key={m} type="button" className="seg" aria-pressed={mode === m} onClick={() => onMode(m)}>
            {t(`live.party.${m}`)}
          </button>
        ))}
      </div>
      {mode === "pick" && (
        <div className="live-avatars" role="group" aria-label={t("live.whoPlays", { count: picked.size })}>
          {ordered.map((m) => {
            const here = inGame.has(m.id);
            const label = `${m.name}${m.id === me ? ` (${t("live.you")})` : ""}, ${here ? t("live.here") : t("live.notHere")}`;
            return (
              <button key={m.id} type="button" className={`live-avatar${picked.has(m.id) ? " is-on" : ""}${here ? " is-here" : ""}`} aria-pressed={picked.has(m.id)} aria-label={label} title={label} onClick={() => onPick(m.id)}>
                <Avatar name={m.name} url={m.avatarUrl} size={30} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------- other screens

function SetupCard() {
  const { t } = useTranslation();
  const setup = useLiveSetup();
  const seen = setup.data?.lastSeenAt;
  return (
    <section className="card live-card">
      <h2 className="panel-title">{t("live.followsTitle")}</h2>
      <p className="muted">{t("live.followsHelp")}</p>
      {setup.data?.otherAccount ? (
        <p className="field-error small">{t("live.otherAccount")}</p>
      ) : seen ? (
        <p className="ok-text small">{t("live.lastSeen", { when: new Date(seen).toLocaleString() })}</p>
      ) : (
        <p className="muted small">{t("live.neverSeen")}</p>
      )}
      <Link to="/live/setup" className="btn btn-secondary">
        {t("live.installConfig")}
      </Link>
    </section>
  );
}

function NoGame({ members, me, onShow, followable }: { members: LiveMember[]; me: string; onShow: (m: Manual) => void; followable: { member: LiveMember; state: LiveState }[] }) {
  const { t } = useTranslation();
  const [map, setMap] = useState<MapId>("de_mirage");
  const [side, setSide] = useState<Side>("t");
  const [players, setPlayers] = useState<Set<string>>(() => new Set(members.map((m) => m.id)));
  useEffect(() => setPlayers(new Set(members.map((m) => m.id))), [members]);
  return (
    <div className="page live-page">
      <LiveHeader map={null} status={t("live.noGame")} tone="off" />
      <SetupCard />
      {followable.length > 0 && (
        <section className="card live-card">
          <h2 className="panel-title">{t("live.teammatesPlaying")}</h2>
          {followable.map(({ member, state }) => (
            <button
              key={member.id}
              type="button"
              className="btn btn-secondary live-follow"
              onClick={() => isMapId(state.map ?? "") && onShow({ map: state.map as MapId, side: state.side ?? "t", players: [member.id, me] })}
            >
              <Avatar name={member.name} url={member.avatarUrl} size={24} />
              {t("live.follow", { name: member.name, map: mapLabel(state.map ?? "") })}
            </button>
          ))}
        </section>
      )}
      <section className="card live-card">
        <h2 className="panel-title">{t("live.byHand")}</h2>
        <h3 className="eyebrow">{t("live.map")}</h3>
        <div className="live-maps" role="group" aria-label={t("live.map")}>
          {MAPS.filter((m) => m.activeDuty).map((m) => (
            <button key={m.id} type="button" className="live-map-choice" aria-pressed={map === m.id} onClick={() => setMap(m.id)}>
              <img src={mapIconUrl(m.id)} alt="" width={32} height={32} />
              {m.name}
            </button>
          ))}
        </div>
        <h3 className="eyebrow">{t("live.sideLabel")}</h3>
        <div className="segmented live-sides" role="group" aria-label={t("live.sideLabel")}>
          {(["t", "ct"] as const).map((s) => (
            <button key={s} type="button" className={`seg side-seg-${s}`} aria-pressed={side === s} onClick={() => setSide(s)}>
              {t(`live.side.${s}`)}
            </button>
          ))}
        </div>
        {members.length > 1 && (
          <>
            <h3 className="eyebrow">{t("live.whoPlays", { count: players.size })}</h3>
            <div className="live-avatars" role="group" aria-label={t("live.whoPlays", { count: players.size })}>
              {members.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`live-avatar${players.has(m.id) ? " is-on" : ""}`}
                  aria-pressed={players.has(m.id)}
                  aria-label={m.name}
                  title={m.name}
                  onClick={() => {
                    const next = new Set(players);
                    if (next.has(m.id)) next.delete(m.id);
                    else next.add(m.id);
                    setPlayers(next);
                  }}
                >
                  <Avatar name={m.name} url={m.avatarUrl} size={36} />
                </button>
              ))}
            </div>
          </>
        )}
        <button type="button" className="btn btn-primary live-show" onClick={() => onShow({ map, side, players: [...players] })}>
          {t("live.show", { map: mapInfo(map).name, side: t(`live.side.${side}`) })}
        </button>
      </section>
    </div>
  );
}

function Warmup({
  scope,
  state,
  me,
  members,
  present,
  onShowMap,
}: {
  scope: string;
  state: LiveState;
  me: string;
  members: LiveMember[];
  present: Set<string>;
  onShowMap: () => void;
}) {
  const { t } = useTranslation();
  const map = state.map as MapId;
  const stuffs = useStuffs(scope, map);
  const executions = useExecutions(scope, map);
  const side = state.side;
  const review = (stuffs.data ?? []).filter((s) => (!side || s.side === side) && (s.myStatus === "practising" || s.myStatus === "to_learn"));
  const mine = (executions.data ?? []).filter((e) => (!side || e.side === side) && e.plan.players.some((p) => p.memberId === me));
  const missing = members.filter((m) => !present.has(m.id));
  return (
    <div className="page live-page">
      <LiveHeader map={state.map} side={state.side} status={t("live.phase.warmup")} />
      <div className="live-warmup-party">
        <div className="live-avatars">
          {members.map((m) => (
            <span key={m.id} className={`live-avatar${present.has(m.id) ? " is-here is-on" : ""}`} title={m.name}>
              <Avatar name={m.name} url={m.avatarUrl} size={30} />
            </span>
          ))}
        </div>
        {missing.length > 0 && members.length > 1 && <p className="muted small">{t("live.notDetected", { names: missing.map((m) => m.name).join(", ") })}</p>}
      </div>
      <section className="card live-card">
        <h2 className="panel-title">{t("live.reviewTitle")}</h2>
        <p className="muted small">{t("live.reviewHelp", { count: review.length, map: mapLabel(map), side: side ? t(`live.side.${side}`) : "" })}</p>
        <ul className="live-review">
          {review.slice(0, 8).map((s) => (
            <li key={s.id}>
              <StuffLink id={s.id} className="live-review-item">
                <GrenadeIcon grenade={s.grenade} side={s.side} size={24} />
                <span>
                  <b>{s.title}</b>
                  <span className="muted small"> · {t(`status.${s.myStatus}`)}</span>
                </span>
                <Icon name="chevronRight" />
              </StuffLink>
            </li>
          ))}
          {mine.map((e) => (
            <li key={e.id}>
              <Link to={`/library/${map}/executions?e=${e.id}&p=me`} className="live-review-item">
                <span className="live-tag">{t("live.exec")}</span>
                <span>
                  <b>{e.title}</b>
                  <span className="muted small"> · {t("live.myPart")}</span>
                </span>
                <Icon name="chevronRight" />
              </Link>
            </li>
          ))}
        </ul>
        <Link to={`/library/${map}/learn`}>{t("live.openLearn")}</Link>
      </section>
      <button type="button" className="btn btn-primary live-show" onClick={onShowMap}>
        {t("live.showMapNow")}
      </button>
    </div>
  );
}

function GameOver({
  scope,
  game,
  onDone,
}: {
  scope: string;
  game: { map: string; score: LiveState["score"]; side: Side | null };
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const map = isMapId(game.map) ? game.map : null;
  const stuffs = useStuffs(scope, map ?? "");
  const setStatus = useSetStatus();
  const [changes, setChanges] = useState<Record<string, LearnStatus>>({});
  const [saving, setSaving] = useState(false);
  const mine = (stuffs.data ?? []).filter((s) => s.myStatus !== null);
  const result = game.score && game.side ? (game.score[game.side] > game.score[game.side === "t" ? "ct" : "t"] ? "win" : game.score.ct === game.score.t ? "draw" : "loss") : null;
  const count = Object.keys(changes).length;
  const save = async () => {
    setSaving(true);
    try {
      for (const [id, status] of Object.entries(changes)) await setStatus.mutateAsync({ id, status });
    } finally {
      setSaving(false);
      onDone();
    }
  };
  return (
    <div className="page live-page">
      <LiveHeader map={game.map} status={t("live.phase.gameover")} tone="off" score={scoreText(game.score, game.side)} sub={result ? t(`live.result.${result}`) : undefined} />
      <div>
        <h2 className="section-title">{t("live.howDidItGo")}</h2>
        <p className="muted">{t("live.howHelp", { count: mine.length })}</p>
      </div>
      <ul className="live-gameover">
        {mine.map((s) => {
          const value = changes[s.id] ?? s.myStatus;
          return (
            <li key={s.id} className="card live-card">
              <div className="row">
                <GrenadeIcon grenade={s.grenade} side={s.side} size={24} />
                <b>{s.title}</b>
              </div>
              <div className="segmented live-status-pick" role="group" aria-label={t("live.statusOf", { title: s.title })}>
                {STATUSES.map((st) => (
                  <button
                    key={st}
                    type="button"
                    className={`seg status-${st}${changes[s.id] === st ? " is-changed" : ""}`}
                    aria-pressed={value === st}
                    onClick={() => {
                      const next = { ...changes };
                      if (st === s.myStatus) delete next[s.id];
                      else next[s.id] = st;
                      setChanges(next);
                    }}
                  >
                    {t(`status.${st}`)}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
        {mine.length === 0 && <li className="muted">{t("live.nothingToRate")}</li>}
      </ul>
      <div className="live-actions">
        <button type="button" className="btn btn-secondary" onClick={onDone}>
          {t("live.skip")}
        </button>
        <button type="button" className="btn btn-primary" disabled={count === 0 || saving} onClick={() => void save()}>
          {count === 0 ? t("live.save") : t("live.saveChanges", { count })}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- page

const DONE_KEY = "69ly:live-done";
/** This long without news from the game, the board offers to pick by hand and to check the setup. */
const QUIET_HELP_MS = 5 * 60_000;
const readDone = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(DONE_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};

/** Live: follows the player's match by itself, falls back to picking by hand. */
export function LivePage() {
  const { t } = useTranslation();
  const { scope } = useCurrentTeam();
  const feed = useLiveFeed(scope);
  const [manual, setManual] = useState<Manual | null>(null);
  const [mapDuringWarmup, setMapDuringWarmup] = useState(false);
  const [party, setParty] = useState<PartyMode>("game");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string[]>(readDone);
  const flipped = useRef<{ side: Side | null; at: number }>({ side: null, at: 0 });

  const me = feed.me;
  const mine = me ? feed.states[me] : undefined;
  // How long since the game last sent: in a match, a few seconds at most.
  const quiet = mine ? feed.now - Date.parse(mine.updatedAt) : Infinity;
  const fresh = quiet < LIVE_STALE_MS;

  // Teammates in this game: same map and score, heard from recently.
  const present = useMemo(() => {
    const out = new Set<string>();
    if (!me) return out;
    out.add(me);
    if (!mine?.map) return out;
    for (const m of feed.members) {
      const s = feed.states[m.id];
      if (m.id !== me && s && isFresh(s, feed.now) && sameMatch(mine, s)) out.add(m.id);
    }
    return out;
  }, [feed.members, feed.states, feed.now, me, mine]);

  // Half-time: the side changed under our feet.
  const side = mine?.side ?? null;
  if (side && flipped.current.side && flipped.current.side !== side) flipped.current = { side, at: feed.now };
  else if (side && !flipped.current.side) flipped.current = { side, at: 0 };
  const justFlipped = !!side && feed.now - flipped.current.at < 30_000;

  const markDone = (key: string) => {
    const next = [...done.slice(-20), key];
    setDone(next);
    try {
      localStorage.setItem(DONE_KEY, JSON.stringify(next));
    } catch {
      // Private window: only for this visit.
    }
  };

  // A game followed (or a map picked by hand): the screen stays on, a quiet spell included.
  useWakeLock(!!manual || (!!mine?.map && quiet < QUIET_HELP_MS));

  if (!feed.connected && !me) return <Loading />;
  if (!me) return <Loading />;

  const players = party === "me" ? new Set([me]) : party === "pick" ? picked : present;
  const togglePick = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const strip = (
    <PartyStrip
      members={feed.members}
      inGame={present}
      me={me}
      mode={party}
      onMode={(m) => {
        if (m === "pick" && picked.size === 0) setPicked(new Set(present));
        setParty(m);
      }}
      picked={picked}
      onPick={togglePick}
    />
  );

  // Picked by hand: same board, no game behind it.
  if (manual) {
    const manualPlayers = party === "me" ? new Set([me]) : party === "pick" ? picked : new Set(manual.players);
    return (
      <div className="page live-page is-board">
        <LiveBoard
          key={`${manual.map}-${manual.side}`}
          scope={scope}
          map={manual.map}
          side={manual.side}
          players={manualPlayers}
          present={new Set(manual.players)}
          members={feed.members}
          me={me}
          party={strip}
          header={
            <LiveTop
              map={manual.map}
              side={manual.side}
              status={t("live.byHandStatus")}
              tone="off"
              members={feed.members}
              present={new Set(manual.players)}
              me={me}
              pick={{ onMap: (map) => setManual({ ...manual, map }), onSide: (side) => setManual({ ...manual, side }) }}
              extra={
                <button type="button" className="btn btn-quiet btn-small live-follow-again" onClick={() => setManual(null)}>
                  {t("live.backToFollow")}
                </button>
              }
            />
          }
        />
      </div>
    );
  }

  const gameKey = `game:${mine?.gameStartedAt ?? ""}`;
  // The game followed, quiet or not: the board stays until the game ends or is forgotten.
  if (mine?.map && quiet < LIVE_FORGET_MS) {
    if (mine.matchPhase === "gameover" && !done.includes(gameKey))
      return <GameOver scope={scope} game={{ map: mine.map, score: mine.score, side: mine.side }} onDone={() => markDone(gameKey)} />;
    if (mine.matchPhase === "warmup" && !mapDuringWarmup && isMapId(mine.map))
      return <Warmup scope={scope} state={mine} me={me} members={feed.members} present={present} onShowMap={() => setMapDuringWarmup(true)} />;
    const map = mine.map;
    const left = bombSecondsLeft(mine, feed.now);
    const pickByHand = () => setManual({ map: isMapId(map) ? map : "de_mirage", side: mine.side ?? "t", players: [...present] });
    const notice =
      feed.connected && quiet >= QUIET_HELP_MS ? (
        <section className="live-notice is-warn live-quiet" role="status">
          <div className="live-quiet-text">
            <b>{t("live.quietTitle")}</b>
            <span>{t("live.quietHelp")}</span>
            <details>
              <summary>{t("live.lostStepsTitle")}</summary>
              <ol className="live-steps">
                <li>{t("live.lostStep1")}</li>
                <li>{t("live.lostStep2")}</li>
                <li>{t("live.lostStep3")}</li>
              </ol>
            </details>
          </div>
          <div className="row">
            <button type="button" className="btn btn-secondary btn-small" onClick={pickByHand}>
              {t("live.pickByHand")}
            </button>
            <Link to="/live/setup" className="btn btn-quiet btn-small">
              {t("live.checkSetup")}
            </Link>
          </div>
        </section>
      ) : mine.matchPhase === "intermission" ? (
        <div className="live-notice" role="status">
          <Icon name="loop" size={14} />
          <span>{t("live.halftimeBanner")}</span>
        </div>
      ) : justFlipped && mine.side ? (
        <div className="live-notice" role="status">
          <Icon name="loop" size={14} />
          <span>{t("live.sidesSwitched", { side: t(`live.sideShort.${mine.side}`) })}</span>
        </div>
      ) : !mine.side ? (
        <p className="live-notice is-quiet">{t("live.sideSoon")}</p>
      ) : null;
    const header = (
      <LiveTop
        map={map}
        side={mine.side}
        status={!feed.connected ? t("live.reconnecting") : fresh ? t("live.fromGame", { phase: roundStatus(t, mine, feed.now) }) : t("live.quietFor", { time: quietFor(t, quiet) })}
        tone={fresh && feed.connected ? "live" : "warn"}
        score={scoreText(mine.score, mine.side)}
        sub={mine.round !== null ? t("live.round", { n: mine.matchPhase === "intermission" ? mine.round + 1 : mine.round + (mine.roundPhase === "over" ? 0 : 1) }) : undefined}
        members={feed.members}
        present={present}
        me={me}
        notice={notice}
      />
    );
    return (
      <div className="page live-page is-board">
        {isMapId(map) ? (
          <LiveBoard
            key={map}
            scope={scope}
            map={map}
            side={mine.side}
            players={players}
            present={present}
            members={feed.members}
            me={me}
            party={strip}
            header={header}
            bomb={left !== null ? { left } : null}
            dim={!fresh}
          />
        ) : (
          <>
            {header}
            <p className="muted">{t("live.unknownMap", { map })}</p>
          </>
        )}
      </div>
    );
  }

  const last = mine?.lastGame;
  const lastKey = `game:${last?.startedAt ?? last?.endedAt ?? ""}`;
  if (last && feed.now - Date.parse(last.endedAt) < LIVE_FORGET_MS && !done.includes(lastKey))
    return <GameOver scope={scope} game={last} onDone={() => markDone(lastKey)} />;

  const followable = feed.members
    .filter((m) => m.id !== me)
    .map((m) => ({ member: m, state: feed.states[m.id] }))
    .filter((x): x is { member: LiveMember; state: LiveState } => !!x.state?.map && isFresh(x.state, feed.now));
  return <NoGame members={feed.members} me={me} followable={followable} onShow={setManual} />;
}

